import { useCallback, useEffect, useMemo, useState } from "react";
import { formatDateTime } from "../../../users/adminUserFormatters";
import {
  AEROCIVIL_CALIBRATION_QA_CHECKLIST,
  AEROCIVIL_CALIBRATION_REFERENCE_POINTS,
  AEROCIVIL_OUTREACH_STATUS_OPTIONS,
} from "../../../../../../constants/aerocivilRadarCalibrationConstants.js";
import {
  nudgeBounds,
  validateBounds,
} from "../../../../../../utils/ideamBartBoundsUtils.js";
import {
  fetchAerocivilGeoref,
  fetchAerocivilLatestFrame,
  fetchAerocivilDiagnostics,
  saveAerocivilCalibration,
  saveAerocivilOutreachStatus,
  saveAerocivilProductionSignoff,
} from "../../../../../../weather/adminRadarActionsApi.js";
import WeatherHealthBadge from "../WeatherHealthBadge";

const NUDGE_STEPS = [0.005, 0.01, 0.02, 0.05];

function BoundsField({ label, value, onChange, error }) {
  return (
    <label className="weather-radar-calibration__field">
      <span>{label}</span>
      <input
        type="number"
        step="0.01"
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
      />
      {error ? <span className="weather-radar-calibration__field-error">{error}</span> : null}
    </label>
  );
}

const WeatherAerocivilCalibrationPanel = ({ canManage = false, onToast }) => {
  const [serverGeoref, setServerGeoref] = useState(null);
  const [latestFrame, setLatestFrame] = useState(null);
  const [previewBounds, setPreviewBounds] = useState(null);
  const [notes, setNotes] = useState("");
  const [boundsConfirmed, setBoundsConfirmed] = useState(false);
  const [outreachStatus, setOutreachStatus] = useState("not_sent");
  const [outreachNotes, setOutreachNotes] = useState("");
  const [manualQaComplete, setManualQaComplete] = useState(false);
  const [productionSignedOff, setProductionSignedOff] = useState(false);
  const [signoffNotes, setSignoffNotes] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [boundsError, setBoundsError] = useState("");

  const validation = useMemo(() => validateBounds(previewBounds), [previewBounds]);
  const [programClosure, setProgramClosure] = useState(null);

  const notify = (message, tone = "success") => {
    onToast?.({ tone, message });
  };

  const loadDiagnostics = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const [georefPayload, framePayload, diagnosticsPayload] = await Promise.all([
        fetchAerocivilGeoref(),
        fetchAerocivilLatestFrame().catch(() => null),
        fetchAerocivilDiagnostics().catch(() => null),
      ]);
      setServerGeoref(georefPayload);
      setProgramClosure(diagnosticsPayload?.programClosure || null);
      setLatestFrame(framePayload);
      setPreviewBounds(georefPayload?.calibration?.bounds || georefPayload?.bounds || null);
      setNotes(georefPayload?.calibration?.notes || "");
      setBoundsConfirmed(georefPayload?.calibration?.boundsConfirmed === true);
      setOutreachStatus(georefPayload?.outreach?.status || "not_sent");
      setOutreachNotes(georefPayload?.outreach?.notes || "");
      setManualQaComplete(georefPayload?.signoff?.manualQaComplete === true);
      setProductionSignedOff(georefPayload?.signoff?.signedOff === true);
      setSignoffNotes(georefPayload?.signoff?.notes || "");
    } catch (error) {
      setServerGeoref(null);
      setProgramClosure(null);
      setLatestFrame(null);
      setErrorMessage(error.message || "Aerocivil georef endpoint unavailable.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDiagnostics();
  }, [loadDiagnostics]);

  const applyBounds = (nextBounds) => {
    const result = validateBounds(nextBounds);
    if (!result.valid) {
      setBoundsError(result.message);
      return;
    }
    setBoundsError("");
    setPreviewBounds(nextBounds);
  };

  const handleSaveCalibration = async () => {
    if (!canManage) {
      return;
    }
    if (!validation.valid) {
      setBoundsError(validation.message);
      return;
    }

    const confirmed = window.confirm(
      "Save calibrated Aerocivil bounds to server config? Previous calibration will be backed up."
    );
    if (!confirmed) {
      return;
    }

    setSaving(true);
    try {
      await saveAerocivilCalibration({
        bounds: previewBounds,
        site: serverGeoref?.site,
        notes,
        boundsConfirmed,
      });
      notify("Aerocivil calibration saved.");
      await loadDiagnostics();
    } catch (error) {
      notify(error.message || "Unable to save calibration.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveOutreach = async () => {
    if (!canManage) {
      return;
    }
    setSaving(true);
    try {
      await saveAerocivilOutreachStatus({
        status: outreachStatus,
        notes: outreachNotes,
      });
      notify("Aerocivil outreach status recorded.");
      await loadDiagnostics();
    } catch (error) {
      notify(error.message || "Unable to save outreach status.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSignoff = async () => {
    if (!canManage) {
      return;
    }
    setSaving(true);
    try {
      const result = await saveAerocivilProductionSignoff({
        signedOff: productionSignedOff,
        boundsConfirmed,
        manualQaComplete,
        notes: signoffNotes,
        checklist: AEROCIVIL_CALIBRATION_QA_CHECKLIST,
      });
      if (result.programClosure) {
        setProgramClosure(result.programClosure);
      }
      notify("Aerocivil production sign-off recorded.");
      await loadDiagnostics();
    } catch (error) {
      notify(error.message || "Unable to record sign-off.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleCopy = async (payload, successMessage) => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
      notify(successMessage);
    } catch {
      window.prompt("Copy calibration output:", JSON.stringify(payload, null, 2));
    }
  };

  return (
    <div className="admin-dash__card panel-card weather-radar-calibration">
      <h2 className="admin-section-title">Aerocivil Georef Calibration & Sign-off</h2>
      <p className="muted">
        Provisional site footprints until Aerocivil metadata or operator calibration. Valley Preferred
        unchanged.
      </p>

      {loading ? <p className="muted">Loading Aerocivil georeference diagnostics…</p> : null}
      {errorMessage ? <p className="weather-radar-health-notice">{errorMessage}</p> : null}

      {serverGeoref ? (
        <>
          <div className="weather-radar-calibration__grid">
            <div>
              <span className="weather-radar-calibration__label">Active georef source</span>
              <strong>{serverGeoref.activeGeorefSource || "—"}</strong>
            </div>
            <div>
              <span className="weather-radar-calibration__label">Site</span>
              <strong>{String(serverGeoref.site || "gta").toUpperCase()}</strong>
            </div>
            <div>
              <span className="weather-radar-calibration__label">Confidence</span>
              <span>{serverGeoref.confidence || "—"}</span>
            </div>
            <div>
              <span className="weather-radar-calibration__label">Bounds confirmed</span>
              <WeatherHealthBadge
                status={serverGeoref.boundsConfirmed ? "healthy" : "degraded"}
              />
            </div>
          </div>

          {serverGeoref.warnings?.length ? (
            <ul className="weather-radar-calibration__warnings">
              {serverGeoref.warnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          ) : null}

          {latestFrame?.publicUrl ? (
            <p className="muted">
              Latest frame: {formatDateTime(latestFrame.timestampUtc)} —{" "}
              <a href={latestFrame.publicUrl} target="_blank" rel="noreferrer">
                open cached PNG
              </a>
            </p>
          ) : null}

          <div className="weather-radar-calibration__bounds">
            <BoundsField
              label="West lng"
              value={previewBounds?.westLng ?? ""}
              onChange={(value) => applyBounds({ ...previewBounds, westLng: value })}
              error={boundsError}
            />
            <BoundsField
              label="South lat"
              value={previewBounds?.southLat ?? ""}
              onChange={(value) => applyBounds({ ...previewBounds, southLat: value })}
            />
            <BoundsField
              label="East lng"
              value={previewBounds?.eastLng ?? ""}
              onChange={(value) => applyBounds({ ...previewBounds, eastLng: value })}
            />
            <BoundsField
              label="North lat"
              value={previewBounds?.northLat ?? ""}
              onChange={(value) => applyBounds({ ...previewBounds, northLat: value })}
            />
          </div>

          <div className="weather-radar-calibration__nudge">
            {NUDGE_STEPS.map((step) => (
              <div key={step} className="weather-radar-calibration__nudge-row">
                <span className="muted">±{step}°</span>
                {["westLng", "eastLng", "southLat", "northLat"].map((field) => (
                  <button
                    key={`${step}-${field}-minus`}
                    type="button"
                    className="btn btn--secondary btn--compact"
                    disabled={!canManage}
                    onClick={() => applyBounds(nudgeBounds(previewBounds, field, -step))}
                  >
                    {field} −
                  </button>
                ))}
                {["westLng", "eastLng", "southLat", "northLat"].map((field) => (
                  <button
                    key={`${step}-${field}-plus`}
                    type="button"
                    className="btn btn--secondary btn--compact"
                    disabled={!canManage}
                    onClick={() => applyBounds(nudgeBounds(previewBounds, field, step))}
                  >
                    {field} +
                  </button>
                ))}
              </div>
            ))}
          </div>

          <label className="weather-radar-calibration__field weather-radar-calibration__field--full">
            <span>Calibration notes</span>
            <textarea
              rows={2}
              value={notes}
              disabled={!canManage}
              onChange={(event) => setNotes(event.target.value)}
            />
          </label>

          <label className="weather-radar-calibration__checkbox">
            <input
              type="checkbox"
              checked={boundsConfirmed}
              disabled={!canManage}
              onChange={(event) => setBoundsConfirmed(event.target.checked)}
            />
            Bounds confirmed by operator (provisional OK until Aerocivil metadata)
          </label>

          <div className="weather-radar-calibration__reference">
            <span className="weather-radar-calibration__label">Reference points</span>
            <ul>
              {AEROCIVIL_CALIBRATION_REFERENCE_POINTS.map((point) => (
                <li key={point.id}>
                  {point.label}: {point.lat.toFixed(3)}, {point.lng.toFixed(3)}
                </li>
              ))}
            </ul>
          </div>

          {canManage ? (
            <div className="weather-radar-calibration__actions">
              <button
                type="button"
                className="btn btn--primary"
                disabled={saving || !validation.valid}
                onClick={handleSaveCalibration}
              >
                {saving ? "Saving…" : "Save calibration bounds"}
              </button>
              <button
                type="button"
                className="btn btn--secondary"
                onClick={() => handleCopy(serverGeoref, "Georef diagnostics copied.")}
              >
                Copy georef JSON
              </button>
            </div>
          ) : null}

          <hr />

          <h3 className="admin-section-title">Outreach tracking</h3>
          <label className="weather-radar-calibration__field">
            <span>Status</span>
            <select
              value={outreachStatus}
              disabled={!canManage}
              onChange={(event) => setOutreachStatus(event.target.value)}
            >
              {AEROCIVIL_OUTREACH_STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className="weather-radar-calibration__field weather-radar-calibration__field--full">
            <span>Notes</span>
            <textarea
              rows={2}
              value={outreachNotes}
              disabled={!canManage}
              onChange={(event) => setOutreachNotes(event.target.value)}
            />
          </label>
          {canManage ? (
            <button
              type="button"
              className="btn btn--secondary"
              disabled={saving}
              onClick={handleSaveOutreach}
            >
              Save outreach status
            </button>
          ) : null}

          <hr />

          <h3 className="admin-section-title">Production sign-off</h3>
          <ul className="weather-radar-calibration__checklist">
            {AEROCIVIL_CALIBRATION_QA_CHECKLIST.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          <label className="weather-radar-calibration__checkbox">
            <input
              type="checkbox"
              checked={manualQaComplete}
              disabled={!canManage}
              onChange={(event) => setManualQaComplete(event.target.checked)}
            />
            Manual QA checklist complete
          </label>
          <label className="weather-radar-calibration__checkbox">
            <input
              type="checkbox"
              checked={productionSignedOff}
              disabled={!canManage}
              onChange={(event) => setProductionSignedOff(event.target.checked)}
            />
            Operator production sign-off recorded
          </label>
          <label className="weather-radar-calibration__field weather-radar-calibration__field--full">
            <span>Sign-off notes</span>
            <textarea
              rows={2}
              value={signoffNotes}
              disabled={!canManage}
              onChange={(event) => setSignoffNotes(event.target.value)}
            />
          </label>
          {canManage ? (
            <button
              type="button"
              className="btn btn--primary"
              disabled={saving}
              onClick={handleSaveSignoff}
            >
              Record production sign-off
            </button>
          ) : null}

          {programClosure ? (
            <>
              <hr />
              <h3 className="admin-section-title">Program closure (A10)</h3>
              <p className="weather-radar-fallback__summary">
                Status: {programClosure.programStatus}
              </p>
              <div className="weather-radar-fallback__sources">
                {programClosure.gates?.map((gate) => (
                  <div key={gate.id} className="weather-radar-fallback__row">
                    <span className="weather-radar-fallback__label">{gate.label}</span>
                    <WeatherHealthBadge
                      status={
                        gate.pass ? "healthy" : gate.status === "manual" ? "degraded" : "down"
                      }
                    />
                  </div>
                ))}
              </div>
            </>
          ) : null}
        </>
      ) : null}
    </div>
  );
};

export default WeatherAerocivilCalibrationPanel;
